import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

export default function SyncEyes() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("instruction"); 
  const [status, setStatus] = useState("System Ready");
  const [isPaused, setIsPaused] = useState(false);
  const [speed, setSpeed] = useState(1); 
  const [position, setPosition] = useState({ x: 50, y: 50 }); 
  
  const requestRef = useRef();
  const startTimeRef = useRef();

  // 1. Movement Animation Logic
  const animate = (time) => {
    if (phase === "calibrating" && !isPaused) {
      if (!startTimeRef.current) startTimeRef.current = time;
      const elapsed = time - startTimeRef.current;

      // Gradually increase speed
      const currentSpeed = 1 + (elapsed / 8000) * 2.5;
      setSpeed(currentSpeed);

      // FULL SCREEN MOVEMENT MATH
      // Radius expanded to 40% (total 80% screen coverage) 
      // Safety margin of 10% on each side to keep it visible
      const xRadius = 40; 
      const yRadius = 40; 
      
      const x = 50 + Math.cos((elapsed / 1200) * currentSpeed) * xRadius;
      const y = 50 + Math.sin((elapsed / 1500) * currentSpeed) * yRadius;

      setPosition({ x, y });

      if (elapsed > 12000) { // Slightly longer for full screen coverage
        setPhase("complete");
        setStatus("Link Established");
      }
    }
    requestRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, [phase, isPaused]);

  // 2. Pause & Focus Logic
  useEffect(() => {
    const handleBlur = () => setIsPaused(true);
    const handleFocus = () => setIsPaused(false);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  const startCalibration = () => {
    setPhase("calibrating");
    setStatus("Syncing Neural Path...");
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#111214] flex flex-col items-center justify-center overflow-hidden select-none ">
      
      {/* PAUSE OVERLAY */}
      {isPaused && (
        <div className="fixed inset-0 z-[200] bg-[#111214]/95 backdrop-blur-xl flex flex-col items-center justify-center">
          <h2 className="text-3xl font-black tracking-[0.3em] text-white uppercase italic">Link Interrupted</h2>
          <button onClick={() => setIsPaused(false)} className="mt-8 px-10 py-3 border border-blue-500/30 text-blue-400 text-[10px] tracking-[0.5em] uppercase">Resume</button>
        </div>
      )}

      {/* BACKGROUND GRID */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" 
           style={{ backgroundImage: 'radial-gradient(#3b82f6 1px, transparent 1px)', backgroundSize: '50px 50px' }} />

      {/* TOP STATUS BAR */}
      <div className="absolute top-10 md:top-14 animate-in fade-in slide-in-from-top-6 duration-1000">
        <div className="inline-flex items-center gap-4 px-5 py-2 rounded-sm border border-white/[0.03] bg-white/[0.02] backdrop-blur-md">
          <p className="text-[10px] text-white tracking-[0.4em] uppercase font-black">
            System <span className="text-zinc-200">Sync</span>
          </p>
          <div className="h-4 w-px bg-zinc-800" />
          <span className="text-[9px] text-blue-400 tracking-[0.2em] uppercase font-bold">{status}</span>
        </div>
      </div>

      {/* THE MOVING POINT (TARGET) - SOFTENED GLOW */}
      {phase === "calibrating" && (
        <div 
          className="absolute transition-transform duration-75 ease-out"
          style={{ 
            left: `${position.x}%`, 
            top: `${position.y}%`,
            transform: 'translate(-50%, -50%)' 
          }}
        >
          <div className="relative flex items-center justify-center">
             {/* Reduced opacity and blur for eye comfort */}
             <div className="absolute w-10 h-10 border border-blue-500/10 rounded-full animate-ping" />
             <div className="absolute w-6 h-6 border border-blue-500/20 rounded-full" />
             
             {/* Core Point - Deep Blue, No Harsh Glow */}
             <div className="w-2 h-2 bg-blue-600 rounded-full shadow-[0_0_8px_rgba(37,99,235,0.3)]" />
          </div>
        </div>
      )}

{/* INSTRUCTION UI */}
{phase === "instruction" && (
  <div className="text-center space-y-8 animate-in zoom-in-95 duration-700 px-6">
    <div className="space-y-4">
      {/* Changed 'Retinal Calibration' to 'Eye Focus Test' */}
      <h2 className="text-2xl md:text-4xl font-light tracking-[0.2em] uppercase text-white">
        Eye Focus <span className="font-bold text-blue-500">Test</span>
      </h2>
      {/* Simplified the technical explanation */}
      <p className="text-[10px] md:text-xs text-zinc-200 tracking-[0.4em] uppercase max-w-md mx-auto leading-relaxed">
        Keep your eyes on the moving dot to sync with the system.
      </p>
    </div>
    <button 
      onClick={startCalibration}
      className="px-12 py-5 border-2 border-blue-500/30 hover:border-blue-500 text-xs tracking-[1em] uppercase font-black text-white transition-all bg-blue-500/5"
    >
      Start Sync
    </button>
  </div>
)}

{/* COMPLETION UI */}
{phase === "complete" && (
  <div className="text-center space-y-8 animate-in fade-in zoom-in-95 duration-1000">
      <div className="w-20 h-20 border-2 border-blue-500/40 rounded-full mx-auto flex items-center justify-center">
        <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
      </div>
      {/* Changed 'Sync Complete' to 'Ready to Play' */}
      <h2 className="text-2xl tracking-[0.5em] uppercase font-black text-white">Ready to Play</h2>
      <button 
      onClick={() => navigate("/game/start")}
      className="px-12 py-5 bg-blue-500 text-white text-xs tracking-[1em] uppercase font-black animate-bounce shadow-[0_0_20px_rgba(59,130,246,0.5)]"
      >
        Start Game
      </button>
  </div>
)}

      {/* FOOTER PILLARS */}
      <div className="absolute bottom-12 animate-in fade-in duration-1000 delay-500">
        <div className="text-[9px] text-zinc-600 tracking-[0.8em] uppercase font-black flex items-center justify-center gap-6">
          <span className={phase === "calibrating" ? "text-blue-500" : ""}>Tracking</span>
          <span className="text-blue-900">•</span>
          <span className={phase === "complete" ? "text-blue-500" : ""}>Success</span>
        </div>
      </div>
    </div>
  );
}